import {
    REPORT_SETTINGS_CONCRETE_REPORT_KEYS,
    REPORT_SETTINGS_DEFAULT_REPORT_KEY,
    ReportSettingsConcreteReportKey,
    ReportSettingsReportKey,
} from "@common/types/reports/report-settings-report-key";
import { getReportSettingsRowVisibilityOptions } from "@common/types/reports/report-settings";
import {
    ReportSettingCandidate,
    ReportSettingsResolutionInput,
    ReportSettingsTarget,
    WorkspaceReportSettingsScope,
    buildReportSettingCandidates,
    toReportSettingsOwner,
} from "./report-setting-candidates";
import {
    ReportSettingKey,
    ReportSettingValues,
    ReportSettingsOwner,
    ReportSettingsStoredRow,
} from "./report-setting-fields";
import { ReportSettingSource } from "./report-setting-source";
import {
    findStoredReportSetting,
    getReportSettingDefaults,
} from "./resolve-report-settings";

export interface ReportSettingsSpot {
    target: ReportSettingsTarget;
    reportKey: ReportSettingsReportKey;
}

export type ReportSettingsSpotInput = Partial<WorkspaceReportSettingsScope> & {
    rows: ReportSettingsStoredRow[];
    spot: ReportSettingsSpot;
};

export interface ReportSettingValueWithSource<K extends ReportSettingKey> {
    value: ReportSettingValues[K];
    source: ReportSettingSource;
}

export interface ReportSettingSpotExplanation<K extends ReportSettingKey> {
    stored: ReportSettingValues[K] | null;
    effective: ReportSettingValueWithSource<K>;
    resetTo: ReportSettingValueWithSource<K> | null;
}

export type ReportSettingsSpotExplanation = {
    [
        K in Exclude<ReportSettingKey, "rowVisibility">
    ]: ReportSettingSpotExplanation<K>;
} & { rowVisibility?: ReportSettingSpotExplanation<"rowVisibility"> };

interface SpotContext {
    input: ReportSettingsSpotInput;
    owner: ReportSettingsOwner;
    candidates: ReportSettingCandidate[];
    defaults: ReportSettingValues;
    defaultSource: ReportSettingSource;
}

export const toSpotResolutionInput = (
    { spot, rows, workspaceId, ...scope }: ReportSettingsSpotInput,
    reportKey: ReportSettingsConcreteReportKey,
): ReportSettingsResolutionInput => {
    const { target } = spot;
    if (target.kind === "template") {
        return { rows, reportKey, target };
    }
    if (workspaceId === undefined) {
        throw new Error("A workspace or entity spot needs a workspace id");
    }
    return { ...scope, rows, reportKey, target, workspaceId };
};

const buildContext = (input: ReportSettingsSpotInput): SpotContext => {
    const { spot, workspaceId } = input;
    const isDefaultSpot = spot.reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY;
    const reportKey =
        spot.reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY
            ? REPORT_SETTINGS_CONCRETE_REPORT_KEYS[0]
            : spot.reportKey;
    const candidates = buildReportSettingCandidates(
        toSpotResolutionInput(input, reportKey),
    ).filter(
        (candidate) =>
            !isDefaultSpot ||
            candidate.source.reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY,
    );
    return {
        input,
        owner: toReportSettingsOwner({ target: spot.target, workspaceId }),
        candidates,
        defaults: getReportSettingDefaults(reportKey),
        defaultSource: { kind: "kick-default", reportKey: spot.reportKey },
    };
};

const explainSetting = <K extends ReportSettingKey>(
    key: K,
    context: SpotContext,
): ReportSettingSpotExplanation<K> => {
    const [spotCandidate, ...below] = context.candidates;
    const stored = spotCandidate.values[key];
    const kickDefault: ReportSettingValueWithSource<K> = {
        value: context.defaults[key],
        source: context.defaultSource,
    };
    const effective =
        findStoredReportSetting(key, context.candidates) ?? kickDefault;
    return {
        stored,
        effective,
        resetTo:
            stored === null
                ? null
                : (findStoredReportSetting(key, below) ?? kickDefault),
    };
};

export const explainReportSettingsSpot = (
    input: ReportSettingsSpotInput,
): ReportSettingsSpotExplanation => {
    const context = buildContext(input);
    const explanation: ReportSettingsSpotExplanation = {
        header: explainSetting("header", context),
        title: explainSetting("title", context),
        subtitle: explainSetting("subtitle", context),
        subtitle2: explainSetting("subtitle2", context),
        footer: explainSetting("footer", context),
        memo: explainSetting("memo", context),
        watermarkText: explainSetting("watermarkText", context),
        logoOption: explainSetting("logoOption", context),
        decimalPlaces: explainSetting("decimalPlaces", context),
        negativeFormat: explainSetting("negativeFormat", context),
        isNegativeInRed: explainSetting("isNegativeInRed", context),
        hasThousandsSeparator: explainSetting("hasThousandsSeparator", context),
        zeroDisplay: explainSetting("zeroDisplay", context),
        currencySymbol: explainSetting("currencySymbol", context),
        currencyPlacement: explainSetting("currencyPlacement", context),
        percentDecimalPlaces: explainSetting("percentDecimalPlaces", context),
        amountScale: explainSetting("amountScale", context),
    };
    return getReportSettingsRowVisibilityOptions(input.spot.reportKey) === null
        ? explanation
        : {
              ...explanation,
              rowVisibility: explainSetting("rowVisibility", context),
          };
};
